/**
 * Transactions Management Page
 * View, filter, and manage payment transactions
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeftRight,
  Calendar,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Eye,
  X,
} from 'lucide-react';
import { Listbox } from '@headlessui/react';
import { realAPI } from '../api/realEndpoints';
import { formatCurrency } from '../utils/helpers';
import { useToast } from '../context/ToastContext';
import Card from '../components/Card';
import Button from '../components/Button';
import './ContentManagement.css';

// Interface for Transaction Data
interface Transaction {
  id: number;
  userId: number;
  providerId: number;
  bookingId: number;
  amount: number;
  platformCharge: number;
  chargeId: string | null;
  paymentStatus: string;
  createdAt: string;
  userInfo: {
    id: number;
    name: string;
    image: string | null;
    email: string;
  };
  providerInfo: {
    id: number;
    name: string;
    image: string | null;
    email: string;
  };
}

// Date Picker Component
const DatePicker = ({
  selectedDate,
  onDateChange,
  onClose,
  isOpen,
  minDate,
  maxDate,
}: {
  selectedDate?: string;
  onDateChange: (date: string) => void;
  onClose: () => void;
  isOpen: boolean;
  minDate?: string;
  maxDate?: string;
}) => {
  const parseDateLocal = (dateStr: string) => {
    if (!dateStr) return new Date();
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(y, m - 1, d);
  };

  const getInitialMonth = () => {
    if (selectedDate) return parseDateLocal(selectedDate);
    if (minDate) return parseDateLocal(minDate);
    return new Date();
  };

  const [currentMonth, setCurrentMonth] = useState<Date>(getInitialMonth());

  const isPreviousDisabled = useMemo(() => {
    if (!minDate) return false;
    const [minY, minM] = minDate.split('-').map(Number);
    return currentMonth.getFullYear() === minY && currentMonth.getMonth() === minM - 1;
  }, [currentMonth, minDate]);

  const isNextDisabled = useMemo(() => {
    if (!maxDate) return false;
    const [maxY, maxM] = maxDate.split('-').map(Number);
    return currentMonth.getFullYear() === maxY && currentMonth.getMonth() === maxM - 1;
  }, [currentMonth, maxDate]);

  useEffect(() => {
    if (isOpen) {
      setCurrentMonth(getInitialMonth());
    }
  }, [isOpen, selectedDate, minDate]);

  if (!isOpen) return null;

  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const renderDays = () => {
    const daysInMonth = getDaysInMonth(currentMonth);
    const firstDay = getFirstDayOfMonth(currentMonth);
    const days = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-8 w-8"></div>);
    }

    // Days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), i);

      // Fix: Use local date components for ISO-style string to avoid timezone shifts
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;

      const isSelected = selectedDate === dateStr;
      const isDisabled = (minDate && dateStr < minDate) || (maxDate && dateStr > maxDate);

      days.push(
        <button
          key={i}
          onClick={() => {
            if (!isDisabled) {
              onDateChange(dateStr);
              onClose();
            }
          }}
          disabled={!!isDisabled}
          className={`h-8 w-8 rounded-full flex items-center justify-center text-sm ${isSelected
            ? 'bg-blue-600 text-white'
            : isDisabled
              ? 'text-gray-300 cursor-not-allowed'
              : 'hover:bg-gray-100 text-gray-700'
            }`}
        >
          {i}
        </button>
      );
    }

    return days;
  };

  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  return (
    <div className="absolute z-10 mt-1 bg-white rounded-lg shadow-lg p-4 w-64 border border-gray-200">
      <div className="flex justify-between items-center mb-2">
        <button
          onClick={() => {
            if (!isPreviousDisabled) {
              setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
            }
          }}
          disabled={isPreviousDisabled}
          className={`p-1 rounded ${isPreviousDisabled ? 'text-gray-300' : 'hover:bg-gray-100'}`}
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <div className="font-medium">
          {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </div>
        <button
          onClick={() => {
            if (!isNextDisabled) {
              setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
            }
          }}
          disabled={isNextDisabled}
          className={`p-1 rounded ${isNextDisabled ? 'text-gray-300' : 'hover:bg-gray-100'}`}
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-xs text-gray-500 mb-2">
        {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day) => (
          <div key={day} className="h-6 flex items-center justify-center">
            {day}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">{renderDays()}</div>
    </div>
  );
};

export default function Transactions() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { error: showError } = useToast();

  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [earnAmount, setEarnAmount] = useState(0);
  const [page, setPage] = useState(1);
  const LIMIT = 10;

  // Filter State
  const [statusFilter, setStatusFilter] = useState('all');

  // Sorting State
  const [sortConfig, setSortConfig] = useState<{
    key: 'amount' | 'platformCharge' | 'paymentStatus' | 'createdAt' | null;
    direction: 'asc' | 'desc';
  }>({
    key: null,
    direction: 'desc',
  });

  // Date filter state
  const [showStartCalendar, setShowStartCalendar] = useState(false);
  const [showEndCalendar, setShowEndCalendar] = useState(false);
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    status: 'all',
  });
  const isFetchingRef = useRef(false);

  const statusOptions = [
    { label: 'All Status', value: 'all' },
    { label: 'Succeeded', value: 'succeeded', color: 'text-green-600' },
    { label: 'Failed', value: 'failed', color: 'text-red-600' },
    { label: 'Pending', value: 'pending', color: 'text-yellow-600' },
    { label: 'Processing', value: 'processing', color: 'text-blue-600' },
    { label: 'Cancelled', value: 'cancelled', color: 'text-gray-600' },
    { label: 'Refunded', value: 'refunded', color: 'text-purple-600' },
  ];

  const fetchTransactions = useCallback(
    async (
      statusOverride?: string,
      fromOverride?: string,
      toOverride?: string,
      sortKeyOverride?: string | null,
      sortDirOverride?: 'asc' | 'desc'
    ) => {
      if (isFetchingRef.current) return; // Prevent multiple simultaneous calls

      isFetchingRef.current = true;
      setIsLoading(true);
      try {
        const offset = (page - 1) * LIMIT;

        let statusToSend: string | undefined =
          (statusOverride !== undefined ? statusOverride : statusFilter) === 'all'
            ? undefined
            : statusOverride !== undefined
              ? statusOverride
              : statusFilter;
        const dateFromToUse = fromOverride !== undefined ? fromOverride : filters.dateFrom;
        const dateToToUse = toOverride !== undefined ? toOverride : filters.dateTo;
        const sortByKey = sortKeyOverride !== undefined ? sortKeyOverride : sortConfig.key;
        const sortByDir = sortDirOverride !== undefined ? sortDirOverride : sortConfig.direction;

        const data = await realAPI.transactions.getList(
          LIMIT,
          offset,
          statusToSend,
          dateFromToUse || undefined,
          dateToToUse || undefined,
          sortByKey || undefined,
          sortByDir
        );

        if (Array.isArray(data)) {
          setTransactions(data);
          setTotal(data.length < LIMIT ? page * LIMIT : page * LIMIT + 1);
        } else if (data && data.results) {
          setTransactions(data.results);
          setTotal(data.counts || 0);
          setEarnAmount(data.totalSucceededAmount || 0)
        } else {
          setTransactions([]);
        }
      } catch (err) {
        console.error('Failed to fetch transactions:', err);
        showError('Error', 'Failed to fetch transactions');
      } finally {
        setIsLoading(false);
        isFetchingRef.current = false;
      }
    },
    [page, statusFilter, filters.dateFrom, filters.dateTo, sortConfig, showError]
  );

  const handleSort = (key: 'amount' | 'platformCharge' | 'paymentStatus' | 'createdAt') => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    setPage(1);
  };

  // Initial fetch - Only if NO dates are set or BOTH are set
  useEffect(() => {
    const hasDates = filters.dateFrom || filters.dateTo;
    const bothDates = filters.dateFrom && filters.dateTo;

    // Only fetch if either no dates are touched, or both are set
    if (!hasDates || bothDates) {
      fetchTransactions();
    }
  }, [page, statusFilter, filters.dateFrom, filters.dateTo, sortConfig]);

  const handleStatusChange = useCallback((val: string) => {
    setStatusFilter(val);
    setFilters((prev) => ({ ...prev, status: val }));
    setPage(1);
  }, []);

  const handleFilterChange = useCallback(
    (key: 'dateFrom' | 'dateTo', value: string) => {
      setFilters((prev) => ({
        ...prev,
        [key]: value,
      }));
      setPage(1);
    },
    [setPage]
  );

  const handleResetFilters = () => {
    setFilters({
      dateFrom: '',
      dateTo: '',
      status: 'all',
    });
    setStatusFilter('all');
    setSortConfig({ key: null, direction: 'desc' });
    setPage(1);
    setShowStartCalendar(false);
    setShowEndCalendar(false);

    // Explicitly call fetch with cleared filters immediately
    fetchTransactions('all', '', '', null, 'desc');
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'succeeded':
        return 'bg-green-100 text-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'processing':
        return 'bg-blue-100 text-blue-800';
      case 'refunded':
        return 'bg-purple-100 text-purple-800';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = useCallback((dateStr: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }, []);

  // Table shimmer component
  const TableShimmer = () => (
    <div className="table-container">
      <div className="table-min-w">
        <div className="grid grid-cols-[0.55fr_1.2fr_1.2fr_1fr_1fr_1.8fr_1fr_1.2fr] gap-2 p-4 pl-3 rounded-t-xl font-semibold text-gray-700 text-[15px] border-b border-gray-200 -ml-2 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 dark:text-white dark:border-gray-600 shadow-sm">
          <div className="text-gray-700 dark:text-white">S. No</div>
          <div className="text-gray-700 dark:text-white">Sender</div>
          <div className="text-gray-700 dark:text-white" style={{ paddingLeft: '5px' }}>
            Receiver
          </div>
          <div className="text-gray-700 dark:text-white" style={{ paddingLeft: '10px' }}>
            Amount
          </div>
          <div className="text-gray-700 dark:text-white" style={{ paddingLeft: '10px' }}>
            Platform Charge
          </div>
          <div className="text-gray-700 dark:text-white" style={{ paddingLeft: '10px' }}>Charge ID</div>
          <div
            className="text-gray-700 dark:text-white flex justify-start"
            style={{ paddingLeft: '15px' }}
          >
            Status
          </div>
          <div className="text-gray-700 dark:text-white whitespace-nowrap" style={{ paddingLeft: '8px' }}>
            Created At
          </div>
        </div>

        {/* Loader in table area */}
        <div className="flex flex-col items-center justify-center py-12">
          <div className="loader"></div>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <Card className="mb-2 p-[10px]">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* Title */}
          <h1 className="text-2xl font-bold text-gray-900 whitespace-nowrap">
            Transactions History
          </h1>

          {/* Filters */}
          <div className="flex flex-wrap lg:items-center lg:justify-end gap-3 w-full">
            {/* Start Date Picker */}
            <div className="relative w-[180px] h-10 flex-shrink-0">
              <button
                onClick={() => {
                  setShowStartCalendar(true);
                  setShowEndCalendar(false);
                }}
                className="w-full h-full pl-10 pr-4 text-left bg-white border border-gray-300 rounded-lg 
          shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 
          transition-all duration-200 hover:border-gray-400 flex items-center"
              >
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                <span className={filters.dateFrom ? 'text-gray-900' : 'text-gray-500'}>
                  {filters.dateFrom
                    ? new Date(filters.dateFrom).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })
                    : 'From Date'}
                </span>
              </button>

              {showStartCalendar && (
                <DatePicker
                  selectedDate={filters.dateFrom}
                  onDateChange={(date) => handleFilterChange('dateFrom', date)}
                  onClose={() => setShowStartCalendar(false)}
                  isOpen
                  maxDate={filters.dateTo || undefined}
                />
              )}
            </div>

            {/* End Date Picker */}
            <div className="relative w-[180px] h-10 flex-shrink-0">
              <button
                onClick={() => {
                  setShowEndCalendar(true);
                  setShowStartCalendar(false);
                }}
                className="w-full h-full pl-10 pr-4 text-left bg-white border border-gray-300 rounded-lg 
          shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 
          transition-all duration-200 hover:border-gray-400 flex items-center"
              >
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                <span className={filters.dateTo ? 'text-gray-900' : 'text-gray-500'}>
                  {filters.dateTo
                    ? new Date(filters.dateTo).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })
                    : 'To Date'}
                </span>
              </button>

              {showEndCalendar && (
                <DatePicker
                  selectedDate={filters.dateTo}
                  onDateChange={(date) => handleFilterChange('dateTo', date)}
                  onClose={() => setShowEndCalendar(false)}
                  isOpen
                  minDate={filters.dateFrom || undefined}
                />
              )}
            </div>

            {/* Status Filter (Dynamic Width) */}
            <div className="relative h-10 flex-shrink-0 transition-all duration-200">
              <Listbox value={statusFilter || 'all'} onChange={handleStatusChange}>
                {({ open }) => (
                  <>
                    <Listbox.Button
                      className="inline-flex items-center h-full pl-4 pr-10 text-left bg-white border border-gray-300
                rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500
                transition-all duration-200 hover:border-gray-400 cursor-pointer
                min-w-[140px] max-w-[400px]"
                    >
                      <span className="block">
                        {statusOptions.find((s) => s.value === (statusFilter || 'all'))?.label ||
                          'Status'}
                      </span>
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    </Listbox.Button>

                    <Listbox.Options
                      className="absolute z-20 mt-1 bg-white rounded-lg shadow-lg border border-gray-200
                min-w-[140px] max-w-[400px]"
                    >
                      {statusOptions.map((option, idx) => (
                        <Listbox.Option
                          key={idx}
                          value={option.value}
                          className={({ active }) =>
                            `px-4 py-2 cursor-pointer transition-colors ${active ? 'bg-gray-100' : ''}`
                          }
                        >
                          {({ selected }) => (
                            <span
                              className={`block truncate ${selected ? 'font-medium' : 'font-normal'} ${option.color || 'text-gray-900'
                                }`}
                            >
                              {option.label}
                            </span>
                          )}
                        </Listbox.Option>
                      ))}
                    </Listbox.Options>
                  </>
                )}
              </Listbox>
            </div>

            {/* Reset Filters */}
            <Button
              variant="secondary"
              onClick={handleResetFilters}
              className="px-4 h-10"
              style={{ width: '162px' }}
            >
              Reset Filters
            </Button>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
        <Card>
          <p className="text-xs text-ink-muted font-semibold uppercase tracking-wider">Total Transactions</p>
          <p className="text-2xl font-bold text-ink mt-2">{total}</p>
        </Card>
        <Card>
          <p className="text-xs text-ink-muted font-semibold uppercase tracking-wider">
            Total Earnings
          </p>
          <p className="text-2xl font-bold text-ink mt-2">
            £
            {Number(earnAmount || 0) % 1 === 0
              ? Number(earnAmount || 0)
              : Number(earnAmount || 0).toFixed(2)}
          </p>
        </Card>
        {/* <Card>
          <p className="text-xs text-ink-muted font-semibold uppercase tracking-wider">Active Subscriptions</p>
          <p className="text-2xl font-bold text-emerald-700 mt-2">{subscriptionCounts.active}</p>
        </Card>
        <Card>
          <p className="text-xs text-ink-muted font-semibold uppercase tracking-wider">Inactive Subscriptions</p>
          <p className="text-2xl font-bold text-red-700 mt-2">{inactiveCounts}</p>
        </Card> */}
      </div>

      <Card className='mt-4'>
        {isLoading ? (
          <TableShimmer />
        ) : transactions.length === 0 ? (
          <div className="table-container">
            <div className="table-min-w">
              {/* Grid Header */}
              <div className="grid grid-cols-[0.55fr_1.2fr_1.2fr_1fr_1fr_1.8fr_1fr_1.2fr] gap-2 p-4 pl-3 rounded-t-xl font-semibold text-gray-700 text-[15px] border-b border-gray-200 -ml-2 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 dark:text-white dark:border-gray-600 shadow-sm">
                <div className="text-gray-700 dark:text-white">S. No</div>
                <div className="text-gray-700 dark:text-white">Sender</div>
                <div className="text-gray-700 dark:text-white" style={{ paddingLeft: '5px' }}>
                  Receiver
                </div>
                <div
                  className="text-gray-700 dark:text-white cursor-pointer hover:text-blue-600 transition-colors flex items-center gap-1"
                  style={{ paddingLeft: '10px' }}
                  onClick={() => handleSort('amount')}
                >
                  Amount{' '}
                  {sortConfig.key === 'amount' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </div>
                <div
                  className="text-gray-700 dark:text-white cursor-pointer hover:text-blue-600 transition-colors flex items-center gap-1"
                  style={{ paddingLeft: '10px' }}
                  onClick={() => handleSort('platformCharge')}
                >
                  Platform Charge{' '}
                  {sortConfig.key === 'platformCharge' &&
                    (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </div>
                <div className="text-gray-700 dark:text-white" style={{ paddingLeft: '10px' }}>
                  Charge ID
                </div>
                <div
                  className="text-gray-700 dark:text-white flex justify-start cursor-pointer hover:text-blue-600 transition-colors items-center gap-1"
                  style={{ paddingLeft: '15px' }}
                  onClick={() => handleSort('paymentStatus')}
                >
                  Status{' '}
                  {sortConfig.key === 'paymentStatus' &&
                    (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </div>
                <div
                  className="text-gray-700 dark:text-white cursor-pointer hover:text-blue-600 transition-colors flex items-center gap-1"
                  style={{ paddingLeft: '8px' }}
                  onClick={() => handleSort('createdAt')}
                >
                  Created At{' '}
                  {sortConfig.key === 'createdAt' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </div>
              </div>
              {/* Empty State inside table */}
              <div className="text-center py-12 bg-gray-50 rounded-b-xl">
                <img src="/NoRecord.svg" alt="No records found" className=" mx-auto mb-4 " />
              </div>
            </div>
          </div>
        ) : (
          <div className="table-container">
            <div className="table-min-w">
              {/* Grid Header */}
              <div className="grid grid-cols-[0.55fr_1.2fr_1.2fr_1fr_1fr_1.8fr_1fr_1.2fr] gap-2 p-4 pl-3 rounded-t-xl font-semibold text-[15px] border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 dark:text-white dark:border-gray-600 shadow-sm">
                <div className="text-gray-700 dark:text-white">S. No</div>
                {/* Sender */}
                <div className="text-gray-700 dark:text-white">Sender</div>

                {/* Receiver */}
                <div className="text-gray-700 dark:text-white">Receiver</div>

                {/* Amount */}
                <div
                  className="text-gray-700 dark:text-white cursor-pointer hover:text-blue-600 transition-colors flex items-center gap-1"
                  onClick={() => handleSort('amount')}
                >
                  Amount{' '}
                  {sortConfig.key === 'amount' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </div>

                {/* Platform Charge */}
                <div
                  className="text-gray-700 dark:text-white cursor-pointer hover:text-blue-600 transition-colors flex items-center gap-1"
                  onClick={() => handleSort('platformCharge')}
                >
                  Platform Charge{' '}
                  {sortConfig.key === 'platformCharge' &&
                    (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </div>

                {/* Charge ID */}
                <div className="text-gray-700 dark:text-white pl-[5px]">Charge ID</div>

                {/* Status */}
                <div
                  className="text-gray-700 dark:text-white flex justify-start cursor-pointer hover:text-blue-600 transition-colors items-center gap-1 pl-[15px]"
                  onClick={() => handleSort('paymentStatus')}
                >
                  Status{' '}
                  {sortConfig.key === 'paymentStatus' &&
                    (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </div>

                {/* Created At */}
                <div
                  className="text-gray-700 dark:text-white cursor-pointer hover:text-blue-600 transition-colors flex items-center gap-1 pl-[8px] whitespace-nowrap"
                  onClick={() => handleSort('createdAt')}
                >
                  Created At{' '}
                  {sortConfig.key === 'createdAt' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </div>
              </div>

              {/* Grid Body */}
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {transactions.map((t, index) => (
                  <div
                    key={t.id}
                    onClick={() => navigate(`/transactions/${t.id}`)}
                    className="grid grid-cols-[0.55fr_1.2fr_1.2fr_1fr_1fr_1.8fr_1fr_1.2fr] items-center gap-2 cursor-pointer hover:bg-blue-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <div className="px-2 py-4 text-sm font-medium text-gray-900 dark:text-white">
                      {(page - 1) * LIMIT + index + 1}
                    </div>

                    {/* Customer */}
                    <div className="px-3 py-4 truncate">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {t.userInfo?.name || 'Unknown'}
                      </div>
                    </div>

                    {/* Provider */}
                    <div className="px-3 py-4 truncate">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {t.providerInfo?.name || 'Unknown'}
                      </div>
                    </div>

                    {/* Amount */}
                    <div className="px-3 py-4 text-sm font-semibold text-gray-900 dark:text-white">
                      {formatCurrency(t.amount)}
                    </div>

                    {/* Platform Charge */}
                    <div className="px-3 py-4 text-sm text-black-500 dark:text-gray-400">
                      {formatCurrency(t.platformCharge)}
                    </div>

                    {/* Charge ID (WIDER) */}
                    <div
                      className="px-3 py-4 text-sm text-black-500 text-black truncate"
                      title={t.chargeId || ''}
                    >
                      {t.chargeId || '-'}
                    </div>

                    {/* Payment Status */}
                    <div className="px-3 py-4 flex items-center">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-md capitalize ${getStatusColor(
                          t.paymentStatus
                        )}`}
                      >
                        {t.paymentStatus}
                      </span>
                    </div>

                    {/* Created At */}
                    <div className="px-3 py-4 text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
                      {formatDate(t.createdAt)}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between p-4 border-t border-gray-200">
              <div className="text-sm text-gray-600">
                Showing {(page - 1) * LIMIT + 1} to {Math.min(page * LIMIT, total)} of {total}{' '}
                transactions
              </div>
              <div className="flex gap-2">
                <div className="flex items-center justify-between gap-4">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="px-4 py-2 text-sm font-medium text-gray-700">
                    Page {page} of {Math.ceil(total / LIMIT)}
                  </span>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setPage((p) => p + 1)}
                    disabled={page * LIMIT >= total}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
